import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import update from 'immutability-helper';
import {
  Row, Col, Modal, Button,
} from 'react-bootstrap';
import QuestionRenderer from '../QuestionRenderer';
import { getRequest, putRequest } from '../../../requestUtils';
import { analyticsTrack } from '../../../analyticsUtils';
import './QuestionsContainer.scss';

const processAnswer = (answer) => (
  answer.map((ans) => {
    const hash = {};
    ans.forEach((a) => { hash[a.column_name] = (a.value && typeof a.value === 'object' && !(a.value instanceof Date) ? a.value.value : a.value); });
    hash.id = ans[0].id;
    return hash;
  })
);

const isValidAnswer = (question) => {
  if (question.question_type === 'proficiency_ranking') {
    return question.answer.some((ans) => ans.ranking);
  }

  return question.answer.length > 0;
};

class QuestionsContainer extends Component {
  constructor(props) {
    super(props);

    // if paginated, the initial state of questionIds should be the first element
    // of paginatedQuestionIds.
    const initialQuestionIds = props.paginated ? props.paginatedQuestionIds[0] : props.questionIds;

    this.state = {
      modalStep: 0,
      questions: [],
      questionIds: initialQuestionIds,
    };

    this.getCurrentQuestions = this.getCurrentQuestions.bind(this);
    this.renderQuestions = this.renderQuestions.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleProficiencyChange = this.handleProficiencyChange.bind(this);
    this.handleProficiencyRankingChange = this.handleProficiencyRankingChange.bind(this);
    this.handleOtherTextChange = this.handleOtherTextChange.bind(this);
    this.updateQuestionAnswer = debounce(this.updateQuestionAnswer.bind(this), 500);
    this.handleCloseModal = this.handleCloseModal.bind(this);
    this.handleModalNext = this.handleModalNext.bind(this);
    this.handleModalBack = this.handleModalBack.bind(this);
    this.isQuestionDisabled = this.isQuestionDisabled.bind(this);
    this.updateQuestionState = this.updateQuestionState.bind(this);
  }

  componentDidMount() {
    this.updateQuestionState();
  }

  getCurrentQuestions() {
    const { questionableId, questionableType } = this.props;
    const { questionIds } = this.state;
    return getRequest(
      '/question_answers/questions',
      {
        questionnaire_question_ids: questionIds,
        questionable_id: questionableId,
        questionable_type: questionableType,
      },
    );
  }

  setModalStep(newModalStep) {
    const { paginatedQuestionIds } = this.props;

    this.setState({
      modalStep: newModalStep,
      questionIds: paginatedQuestionIds[newModalStep],
    }, this.updateQuestionState);
  }

  getHandleChangeCallback(question) {
    switch (question.question_type) {
      case 'proficiency': return this.handleProficiencyChange;
      case 'proficiency_ranking': return this.handleProficiencyRankingChange;
      default: return this.handleInputChange;
    }
  }

  handleCloseModal() {
    const { closeModalCallback, paginated } = this.props;

    if (paginated) {
      this.setModalStep(0); // will call updateQuestionState as a setState callback
    } else {
      this.updateQuestionState();
    }
    closeModalCallback();
  }

  handleModalBack() {
    const { modalStep } = this.state;

    if (modalStep === 0) return;
    this.setModalStep(modalStep - 1);
  }

  handleModalNext() {
    const { modalStep } = this.state;

    if (this.shouldCloseModal()) {
      this.handleCloseModal();
    } else {
      this.setModalStep(modalStep + 1);
    }
  }

  handleInputChange(value, key, name, debounceUpdate = true) {
    const {
      questionsUpdated, questionableId, questionableType,
      csrfToken, questionnaireName, handleToggleSave,
    } = this.props;
    const { questions } = this.state;
    let question;
    if (key === 'project lead about') {
      question = questions.find((q) => q.question_key === 'project_lead_about');
    } else {
      question = questions.find((q) => q.question_key === key);
    }
    let errorFound = false;
    switch (question.question_type) {
      case 'list':
        question.answer = value.filter(Boolean);
        break;
      case 'location':
        handleToggleSave(true);
        question.answer = value;
        break;
      case 'numeric_range':
        if (question.answer === null) question.answer = {};
        question.answer[name] = value;
        if ((name === 'low' && parseInt(question.answer.high, 10) < parseInt(value, 10))
          || (name === 'high' && parseInt(value, 10) < parseInt(question.answer.low, 10))) {
          errorFound = true;
        }
        break;
      case 'date_range':
        if (question.answer === null) question.answer = {};
        question.answer[name] = value;
        break;
      case 'select_one':
      case 'only_custom_tags':
      case 'select_many':
      case 'proficiency_ranking':
      case 'freetext':
      case 'number':
      case 'past_roles':
      case 'boolean':
      case 'group_parent':
      default:
        question.answer = value;
    }

    this.setState({ questions });
    questionsUpdated();
    if (errorFound) {
      return null;
    }
    if (debounceUpdate) {
      return this.updateQuestionAnswer(question);
    }
    let ans = question.answer;
    if (question.question_type === 'group_parent') {
      ans = processAnswer(question.answer);
    }
    return putRequest(
      `/question_answers/${question.id}`,
      {
        questionable_type: questionableType,
        questionable_id: questionableId,
        question_answer: { answer: { [question.question_type]: ans } },
        questionnaire_name: questionnaireName,
      },
      csrfToken,
    );
  }

  handleProficiencyChange(newProficiency, key, idx) {
    const {
      questionableType,
      questionableId,
      questionsUpdated,
      csrfToken,
      questionnaireName,
    } = this.props;
    const { questions } = this.state;
    const question = questions.find((q) => q.question_key === key);
    const currentAnswer = question.answer[idx];
    const newAnswer = update(currentAnswer, {
      proficiency: { $set: newProficiency },
    });
    question.answer[idx] = newAnswer;
    this.setState({ questions });
    questionsUpdated();
    return putRequest(
      `/question_answers/${question.id}`,
      {
        questionable_type: questionableType,
        questionable_id: questionableId,
        question_answer: { answer: { proficiency: newAnswer } },
        questionnaire_name: questionnaireName,
      },
      csrfToken,
    );
  }

  handleProficiencyRankingChange(questionKey, ranked, unranked) {
    const {
      questionableType,
      questionableId,
      questionsUpdated,
      csrfToken,
      questionnaireName,
    } = this.props;
    const { questions } = this.state;

    const question = questions.find((q) => q.question_key === questionKey);
    question.answer = ranked.concat(unranked);
    this.setState({ questions });

    questionsUpdated();
    return putRequest(
      `/question_answers/${question.id}`,
      {
        questionable_type: questionableType,
        questionable_id: questionableId,
        question_answer: { answer: { ranked, unranked } },
        questionnaire_name: questionnaireName,
      },
      csrfToken,
    );
  }

  handleOtherTextChange(value, key) {
    const { questions } = this.state;
    const question = questions.find((q) => q.question_key === key);
    const other = question.answer.find((x) => typeof x.value === 'string' && x.value.includes('custom:'));
    if (other) {
      other.label = value;
      other.value = `custom:${value}`;
    } else {
      const otherValue = { label: value, value: `custom:${value}` };
      question.answer.push(otherValue);
    }

    this.setState({ questions });
    this.updateQuestionAnswer(question);
  }

  isQuestionDisabled(question) {
    const { questions } = this.state;

    if (question.parent_id === null
      || question.question_type === 'proficiency_ranking'
      || question.question_type === 'proficiency'
    ) return false;
    const parent = questions.find((q) => q.question_id === question.parent_id);

    if (parent && parent.question_type === 'select_one' && parent.answer) {
      const parentAnswer = parent.answer.value;

      if (question.trigger_type === 'hide' && parentAnswer === 'yes') {
        return true;
      } if ((question.trigger_type === 'show' || question.trigger_type === 'disable') && parentAnswer === 'no') {
        return true;
      }
      return false;
    } if (parent && parent.question_type === 'boolean') {
      if ((question.trigger_type === 'hide' || question.trigger_type === 'disable') && parent.answer === 'true') {
        return true;
      }
      return false;
    }
    return true;
  }

  updateQuestionAnswer(question) {
    const {
      csrfToken,
      questionnaireName,
      suggestedAnswers,
      questionableId,
      questionableType,
      handleToggleSave,
    } = this.props;
    return putRequest(
      `/question_answers/${question.id}`,
      {
        questionable_type: questionableType,
        questionable_id: questionableId,
        question_answer: { answer: { [question.question_type]: question.answer } },
        questionnaire_name: questionnaireName,
      },
      csrfToken,
    ).then(() => {
      const properties = { question_key: question.question_key, location: questionnaireName };
      const suggestedAnswer = suggestedAnswers
                              && suggestedAnswers[question.question_key]
                              && suggestedAnswers[question.question_key.map((s) => s.name)];
      if (question.question_type === 'select_many' && suggestedAnswer
        && question.answer.some((item) => suggestedAnswer.includes(item.label))) {
        properties.picked_suggested_answer = true;
      }
      // Update Roles question so options can reflect selected AOE's
      if (question.question_key === 'functional_areas' && questionnaireName === 'Consultant Onboarding') {
        this.updateQuestionState();
      }
      analyticsTrack('Answered a Question', properties);
      this.props.handleMenuUpdate(true);
      handleToggleSave(false);
    });
  }

  updateQuestionState() {
    this.getCurrentQuestions()
      .then((response) => {
        this.setState({ questions: response.data });
        this.props.loadButtons();
        if (response.data && response.data.some((question) => question.answer)) {
          this.props.questionsUpdated();
        }
      });
  }

  shouldCloseModal() {
    const { paginatedQuestionIds, paginated } = this.props;
    const { modalStep, questions } = this.state;

    if (!paginated || paginatedQuestionIds.length === 0) {
      // error handling/catching - not a paginated question or no paginatedQuestions to display
      return true;
    } if (modalStep === paginatedQuestionIds.length - 1) {
      // on last modal step, so close modal
      return true;
    }
    if (questions.every((q) => !q.paginated_modal || !isValidAnswer(q))) {
      // all answers given are invalid on this page, so close modal.
      return true;
    } if (questions.some((q) => this.isQuestionDisabled(q))) {
      // some question on current page is disabled, so close modal rather than going to next step.
      return true;
    }

    // default, do not close modal
    return false;
  }

  renderQuestions() {
    const { questions, modalStep } = this.state;
    const {
      csrfToken,
      owner,
      forceSmallQuestionTitle,
      suggestedAnswers,
      saveOnChange,
      questionnaireName,
      handleMenuUpdate,
      userId,
      loadButtons,
      handleNewResume,
      handleToggleSave,
      objectId,
      isReadingMore,
      handleResumeUpload,
      handlePastRolesAfterUpdate,
    } = this.props;
    const onboardingQuestionnaire = questionnaireName === 'Consultant Onboarding';

    return (
      <div className="questions-container">
        <div className={`question-renderer ${onboardingQuestionnaire ? 'onboarding-questionnaire' : ''}`}>
          {questions.map((q) => (
            <QuestionRenderer
              question={q}
              handleChange={this.getHandleChangeCallback(q)}
              handleOtherTextChange={this.handleOtherTextChange}
              csrfToken={csrfToken}
              key={`${q.id}-${modalStep}`}
              disabled={this.isQuestionDisabled(q)}
              owner={owner}
              userId={userId}
              closeModal={this.handleCloseModal}
              forceSmallQuestionTitle={forceSmallQuestionTitle}
              suggestedAnswers={suggestedAnswers}
              saveOnChange={saveOnChange}
              questionnaireName={questionnaireName}
              handleMenuUpdate={handleMenuUpdate}
              loadButtons={loadButtons}
              handleNewResume={handleNewResume}
              updateParentError={handleToggleSave}
              objectId={objectId}
              isReadingMore={isReadingMore}
              handleResumeUpload={handleResumeUpload}
              handlePastRolesAfterUpdate={handlePastRolesAfterUpdate}
            />
          ))}
        </div>
      </div>
    );
  }

  renderNavButtons() {
    const { paginatedQuestionIds, paginated } = this.props;
    const { questions, modalStep } = this.state;

    if (!paginated) {
      return (
        <Button
          variant="primary"
          onClick={this.handleCloseModal}
        >
          Save
        </Button>
      );
    }

    if (!questions || !questions.length) return null;
    const nextDisabled = questions.reduce((aggr, question) => {
      const answerMissing = (question.question_type === 'proficiency')
        ? question.answer.some((a) => !a.proficiency)
        : false;

      return aggr || answerMissing;
    }, false);

    // If not on the last step of a paginated question
    // button gets rendered in the middle.
    if (modalStep === 0) {
      return (
        <Button variant="primary" onClick={this.handleModalNext} disabled={nextDisabled}>
          Next
        </Button>
      );
    }

    // last step (proficiency nav buttons: Save, Back, Skip)
    return (
      <Fragment>
        <Row>
          <Col xs={6}>
            <Button variant="secondary back-button" onClick={this.handleModalBack}>
              Back
            </Button>
          </Col>
          <Col xs={6}>
            <Button
              variant="primary save-button"
              onClick={this.handleModalNext}
              disabled={nextDisabled}
            >
              {modalStep < paginatedQuestionIds.length - 1 ? 'Next' : 'Save'}
            </Button>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <Button variant="secondary skip-button" onClick={this.handleCloseModal}>
              Skip
            </Button>
          </Col>
        </Row>
      </Fragment>
    );
  }

  render() {
    const {
      withModal, hideFooterButton, questionType, modalOpen, forceCloseButton,
    } = this.props;

    if (withModal) {
      return (
        <Modal
          show={modalOpen}
          onHide={this.handleCloseModal}
          animation={false}
          keyboard={!forceCloseButton}
          backdrop={forceCloseButton ? 'static' : true}
          size="lg"
        >
          <Modal.Header closeButton className="no-border-bottom" />
          <Modal.Body className={`style-guide ${hideFooterButton && 'hidden-footer'}`}>
            <div className="main-container">
              {this.renderQuestions()}
            </div>
          </Modal.Body>
          <Modal.Footer className={`questions-container-nav-buttons justify-content-center ${questionType} ${hideFooterButton || questionType === 'group_parent' ? 'd-none' : ''}`}>
            { this.renderNavButtons() }
          </Modal.Footer>
        </Modal>
      );
    }
    return (
      <Fragment>
        {this.renderQuestions()}
      </Fragment>
    );
  }
}

QuestionsContainer.propTypes = {
  questionnaireName: PropTypes.string.isRequired,
  questionIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  objectId: PropTypes.number,
  questionType: PropTypes.string,
  questionableId: PropTypes.number.isRequired,
  questionableType: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  owner: PropTypes.bool,
  forceSmallQuestionTitle: PropTypes.bool,
  questionsUpdated: PropTypes.func,
  suggestedAnswers: PropTypes.shape([]),
  saveOnChange: PropTypes.bool,
  withModal: PropTypes.bool,
  hideFooterButton: PropTypes.bool,
  closeModalCallback: PropTypes.func,
  modalOpen: PropTypes.bool,
  paginated: PropTypes.bool,
  paginatedQuestionIds: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  handleMenuUpdate: PropTypes.func,
  userId: PropTypes.number,
  loadButtons: PropTypes.func,
  handleNewResume: PropTypes.func,
  handleToggleSave: PropTypes.func,
  forceCloseButton: PropTypes.bool,
  isReadingMore: PropTypes.bool,
  handleResumeUpload: PropTypes.func,
  handlePastRolesAfterUpdate: PropTypes.func,
};

QuestionsContainer.defaultProps = {
  questionType: '',
  objectId: null,
  owner: false,
  forceSmallQuestionTitle: false,
  questionsUpdated: () => {},
  suggestedAnswers: null,
  saveOnChange: false,
  withModal: false,
  hideFooterButton: false,
  paginatedQuestionIds: [],
  closeModalCallback: () => {},
  modalOpen: false,
  paginated: false,
  handleMenuUpdate: () => {},
  userId: null,
  loadButtons: () => {},
  handleNewResume: () => {},
  handleResumeUpload: () => {},
  handleToggleSave: () => {},
  forceCloseButton: false,
  isReadingMore: false,
  handlePastRolesAfterUpdate: () => {},
};

export default QuestionsContainer;
